import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Container, Box, Typography, CircularProgress, Alert, Snackbar, Button, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentIcon from '@mui/icons-material/Payment';
import ChatIcon from '@mui/icons-material/Chat';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { ITransactionFullDetails } from 'features/transaction-details/types';
import { getTransactionFullDetails } from 'features/transaction-details/services';
import { TransactionHeader } from '../TransactionHeader';
import { TransactionFinalizationsDetail } from '../TransactionFinalizationsDetail';
import { AuthorizationCard } from '../AuthorizationCard';
import { getOperators, finalizeTransaction } from 'features/transactions/services';
import { CreateAuthorizationModal } from 'features/authorization/components/CreateAuthorizationModal';
import { ConfirmationModal } from 'features/common/components/ConfirmationModal/ConfirmationModal';
import { CreateETicketModal } from 'features/authorization/components/CreateETicketModal';
import { UnbindCrossConfirmModal } from '../UnbindCrossConfirmModal';
import { ICrossWithServiceInfo } from 'features/transaction-details/types';
import { TransactionViewContext } from 'features/transaction-details/types/transactionCrossContext';
import { CreateRefundAuthorizationModal } from 'features/transactions/components/CreateRefundAuthorizationModal';
import { AuthorizationAmadeusFPCashCommandModal } from 'features/transactions/components/AuthorizationAmadeusFPCashCommandModal';
import {
	computeTransactionAmountSummary,
	copyToClipboard,
	formatTransactionAmount,
	resolveSellerEmailByUsername,
} from 'features/transaction-details/helpers';
import { computeAuthorizationBalances } from 'features/transaction-details/helpers/authorizationBalances';
import {
	authorizationHasEticket,
	findAuthorizationIdForEticket,
} from 'features/transaction-details/helpers/authorizationTickets';
import { getLockedCrossOperatorForAuthorization } from 'features/authorization/helpers/crossOperatorLock';
import { DLocalCaptureModal } from '../DLocalCaptureModal';
import { DLocalCaptureDisclaimerModal } from '../DLocalCaptureDisclaimerModal';
import { canUseDLocalCapture } from 'features/transaction-details/helpers/canUseDLocalCapture';
import { computeDLocalFeeAmount, DLOCAL_FEE_RATE } from 'features/transaction-details/constants/dlocalCapture';
import { buildTransactionContextText } from 'features/transaction-details/helpers/buildTransactionContextText';

interface TransactionDetailsViewProps {
	transactionId: number;
	highlightAuthorizationId?: number;
	highlightEticket?: string;
}

export const TransactionDetailsView = ({
	transactionId,
	highlightAuthorizationId,
	highlightEticket,
}: TransactionDetailsViewProps): JSX.Element => {
	const history = useHistory();
	const { auth } = useSelector((state: RootState) => state);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [details, setDetails] = useState<ITransactionFullDetails | null>(null);
	const [operators, setOperators] = useState<{ value: string; description: string }[]>([]);
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; duration?: number }>({
		open: false,
		message: '',
	});

	const showSnackbar = (message: string, duration = 2000): void => {
		setSnackbar({ open: true, message, duration });
	};

	const highlightRef = useRef<HTMLDivElement>(null);
	const newAuthButtonRef = useRef<HTMLButtonElement>(null);
	const [showCreateAuthorizationModal, setShowCreateAuthorizationModal] = useState(false);
	const [showFinalizeModal, setShowFinalizeModal] = useState(false);
	const [processingFinalize, setProcessingFinalize] = useState(false);
	const [selectedAuthId, setSelectedAuthId] = useState<number | null>(null);
	const [showETicketModal, setShowETicketModal] = useState(false);
	const [unbindTarget, setUnbindTarget] = useState<{
		cross: ICrossWithServiceInfo;
		authorizationId: number;
	} | null>(null);
	const [showRefundModal, setShowRefundModal] = useState(false);
	const [showCommandModal, setShowCommandModal] = useState(false);
	const [showDLocalDisclaimerModal, setShowDLocalDisclaimerModal] = useState(false);
	const [showDLocalCaptureModal, setShowDLocalCaptureModal] = useState(false);
	const [showDLocalFinalizeBanner, setShowDLocalFinalizeBanner] = useState(false);
	const [openingGoogleChat, setOpeningGoogleChat] = useState(false);
	const deepLinkValidatedRef = useRef(false);

	const resolvedHighlightAuthorizationId = useMemo((): number | undefined => {
		if (!details) {
			return highlightAuthorizationId;
		}
		if (highlightAuthorizationId != null) {
			const authExists = details.authorizations.some((a) => a.id === highlightAuthorizationId);
			if (authExists) {
				return highlightAuthorizationId;
			}
		}
		if (highlightEticket) {
			return findAuthorizationIdForEticket(details.authorizations, highlightEticket);
		}
		return undefined;
	}, [details, highlightAuthorizationId, highlightEticket]);

	useEffect(() => {
		deepLinkValidatedRef.current = false;
	}, [transactionId, highlightAuthorizationId, highlightEticket]);

	const loadData = useCallback(
		async (options?: { silent?: boolean }): Promise<void> => {
			const silent = options?.silent ?? false;

			if (!silent) {
				setLoading(true);
				setError(null);
			}

			try {
				const [transactionData, operatorsData] = await Promise.all([
					getTransactionFullDetails(transactionId),
					getOperators(),
				]);

				setDetails(transactionData);
				setOperators(operatorsData.data);
			} catch (err) {
				console.error('Error al cargar detalles:', err);
				if (silent) {
					setSnackbar({
						open: true,
						message: 'El boleto se desvinculó, pero no se pudieron refrescar los datos. Recargá la página.',
						duration: 4000,
					});
				} else {
					setError('Error al cargar los detalles de la transacción');
				}
			} finally {
				if (!silent) {
					setLoading(false);
				}
			}
		},
		[transactionId],
	);

	useEffect(() => {
		loadData();
	}, [loadData]);

	useEffect(() => {
		if (resolvedHighlightAuthorizationId && highlightRef.current) {
			const timer = window.setTimeout(() => {
				highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}, 300);
			return () => window.clearTimeout(timer);
		}
		return undefined;
	}, [resolvedHighlightAuthorizationId, details]);

	// Validar deep links (?highlightAuth= / ?highlightEticket=) una vez cargados los datos
	useEffect(() => {
		if (loading || !details || deepLinkValidatedRef.current) return;
		if (highlightAuthorizationId == null && !highlightEticket) return;

		deepLinkValidatedRef.current = true;
		const warnings: string[] = [];

		if (highlightAuthorizationId != null) {
			const authExists = details.authorizations.some((a) => a.id === highlightAuthorizationId);
			if (!authExists) {
				warnings.push(`Autorización #${highlightAuthorizationId} no encontrada en esta transacción.`);
			}
		}

		if (highlightEticket) {
			const authScope =
				highlightAuthorizationId != null
					? details.authorizations.find((a) => a.id === highlightAuthorizationId)
					: undefined;
			const ticketFound = authScope
				? authorizationHasEticket(authScope, highlightEticket)
				: Boolean(findAuthorizationIdForEticket(details.authorizations, highlightEticket));

			if (!ticketFound) {
				if (highlightAuthorizationId != null && authScope) {
					warnings.push(`E-Ticket ${highlightEticket} no encontrado en la autorización #${highlightAuthorizationId}.`);
				} else {
					warnings.push(`E-Ticket ${highlightEticket} no encontrado en esta transacción.`);
				}
			}
		}

		if (warnings.length > 0) {
			setSnackbar({ open: true, message: warnings.join(' '), duration: 5000 });
		}
	}, [loading, details, highlightAuthorizationId, highlightEticket]);

	// Focus inicial en "Nueva Autorización" solo sin deep link de autorización/e-ticket
	useEffect(() => {
		if (!loading && details && newAuthButtonRef.current && !resolvedHighlightAuthorizationId && !highlightEticket) {
			const timer = window.setTimeout(() => {
				newAuthButtonRef.current?.focus();
			}, 300);
			return () => window.clearTimeout(timer);
		}
		return undefined;
	}, [loading, details, resolvedHighlightAuthorizationId, highlightEticket]);

	const handleCopySuccess = (message: string): void => {
		showSnackbar(message);
	};

	const handleCloseSnackbar = (): void => {
		setSnackbar({ open: false, message: '' });
	};

	const handleBack = (): void => {
		history.goBack();
	};

	const handleCreateAuthorization = (): void => {
		setShowCreateAuthorizationModal(true);
	};

	const handleFinalizeTransaction = async (): Promise<void> => {
		setProcessingFinalize(true);
		try {
			await finalizeTransaction(transactionId);
			setSnackbar({ open: true, message: 'Transacción finalizada con éxito' });
			setShowFinalizeModal(false);
			setShowDLocalFinalizeBanner(false);
			await loadData(); // Recargar datos
		} catch (err) {
			console.error('Error al finalizar transacción:', err);
			setSnackbar({ open: true, message: 'Error al finalizar la transacción' });
		} finally {
			setProcessingFinalize(false);
		}
	};

	const handleAuthorizationCreated = (): void => {
		setShowCreateAuthorizationModal(false);
		setSnackbar({ open: true, message: 'Autorización creada con éxito' });
		loadData(); // Recargar datos
	};

	const handleAddETicket = (authId: number): void => {
		setSelectedAuthId(authId);
		setShowETicketModal(true);
	};

	const handleUnbindCross = (authorizationId: number, cross: ICrossWithServiceInfo): void => {
		setUnbindTarget({ cross, authorizationId });
	};

	const handleRefund = (authId: number): void => {
		setSelectedAuthId(authId);
		setShowRefundModal(true);
	};

	const handleGenerateCommand = (authId: number): void => {
		setSelectedAuthId(authId);
		setShowCommandModal(true);
	};

	const selectedAuthorizationForRefund = details?.authorizations.find((auth) => auth.id === selectedAuthId);

	const selectedAuthorizationCrossContext = useMemo(() => {
		if (!selectedAuthorizationForRefund) {
			return { pendingToCross: 0, coinCode: 'USD' as const };
		}
		const balances = computeAuthorizationBalances(selectedAuthorizationForRefund);
		return {
			pendingToCross: balances.pendingToCross,
			coinCode: selectedAuthorizationForRefund.coinCode,
		};
	}, [selectedAuthorizationForRefund]);

	const lockedCrossOperatorCode = useMemo(() => {
		if (!selectedAuthId || !details) return null;
		const auth = details.authorizations.find((a) => a.id === selectedAuthId);
		if (!auth?.crossesData?.length) return null;
		return getLockedCrossOperatorForAuthorization(auth.crossesData)?.operatorCode ?? null;
	}, [selectedAuthId, details]);

	const transactionViewContext = useMemo((): TransactionViewContext | undefined => {
		if (!details) return undefined;
		const td = details.transaction.transactionData;
		return {
			transactionId,
			transactionDealId: td.crmTicket,
			cardholder: td.cardholder,
			coinCode: td.coinCode,
			isBitrixDeal: td.isBitrixDeal,
		};
	}, [details, transactionId]);

	const selectedRefundLimits = useMemo(() => {
		if (!selectedAuthorizationForRefund) return undefined;
		const balances = computeAuthorizationBalances(selectedAuthorizationForRefund);
		return {
			maxRefundable: balances.maxRefundable,
			activeCrossed: balances.activeCrossed,
			authorizationCode: selectedAuthorizationForRefund.authorizationCode,
		};
	}, [selectedAuthorizationForRefund]);

	const handleModalClose = (message?: string): void => {
		setShowETicketModal(false);
		setUnbindTarget(null);
		setShowRefundModal(false);
		setShowCommandModal(false);
		setSelectedAuthId(null);
		if (message) {
			setSnackbar({ open: true, message });
			// Solo recargar datos si hay un mensaje de éxito
			loadData();
		}
	};

	const finalizeCopy = useMemo(() => {
		if (!details) return null;

		const { amount, pendingAuthorization, pendingToCross, coinCode } = details.transaction.transactionData;
		const summary = computeTransactionAmountSummary(amount, pendingAuthorization, pendingToCross, coinCode);
		const formattedTotal = formatTransactionAmount(summary.totalAuthorized, summary.coinCode);

		return {
			tooltip: `Ajusta el monto de la transacción al total autorizado. El pendiente a autorizar quedará en 0 y esta transacción tendrá un monto total de ${formattedTotal}.`,
			confirm: `¿Estás seguro de finalizar esta transacción? El monto se ajustará al total autorizado y el pendiente a autorizar quedará en 0. Esta transacción tendrá un monto total de ${formattedTotal}.`,
		};
	}, [details]);

	const showDLocalCaptureButton = useMemo(() => {
		if (!details) return false;
		return canUseDLocalCapture(details.transaction.transactionData.pendingAuthorization, auth.country, auth.user?.role);
	}, [details, auth.country, auth.user?.role]);

	const dlocalFeeTooltip = useMemo(() => {
		if (!details) return '';
		const { pendingAuthorization, coinCode } = details.transaction.transactionData;
		const fee = computeDLocalFeeAmount(pendingAuthorization);
		return `Costo DLocal ${(DLOCAL_FEE_RATE * 100).toFixed(1)}%: ${formatTransactionAmount(
			fee,
			coinCode,
		)} sobre ${formatTransactionAmount(pendingAuthorization, coinCode)}`;
	}, [details]);

	const handleDLocalDisclaimerConfirm = (): void => {
		setShowDLocalDisclaimerModal(false);
		setShowDLocalCaptureModal(true);
	};

	const handleDLocalPaymentSuccess = (): void => {
		setShowDLocalFinalizeBanner(true);
		setSnackbar({
			open: true,
			message: 'Pago DLocal confirmado. Finalizá la transacción para cerrar el pendiente de autorización.',
			duration: 6000,
		});
		void loadData({ silent: true });
	};

	const showGoogleChatWithSellerButton = useMemo(() => {
		const createdBy = details?.saleOrder?.createdBy;
		if (!createdBy) return false;
		if (!auth.user?.username) return true;
		return createdBy !== auth.user.username;
	}, [details?.saleOrder?.createdBy, auth.user?.username]);

	const handleCopyTransactionContext = async (): Promise<void> => {
		if (!details) return;

		const contextText = buildTransactionContextText(details, transactionId);
		const copied = await copyToClipboard(contextText);

		if (copied) {
			showSnackbar('Contexto copiado al portapapeles', 3000);
		} else {
			showSnackbar('No se pudo copiar el contexto', 3000);
		}
	};

	const handleOpenGoogleChatWithSeller = async (): Promise<void> => {
		const createdBy = details?.saleOrder?.createdBy;
		if (!createdBy) return;

		setOpeningGoogleChat(true);
		try {
			const sellerEmail = await resolveSellerEmailByUsername(createdBy);
			if (!sellerEmail) {
				showSnackbar(`No se encontró el email del vendedor (${createdBy})`);
				return;
			}

			const copied = await copyToClipboard(sellerEmail);
			window.open('https://chat.google.com/', '_blank', 'noopener,noreferrer');

			if (copied) {
				showSnackbar(
					`Email de ${createdBy} copiado. Pegalo en "Nuevo chat" de Google Chat para iniciar la conversación.`,
					6000,
				);
			} else {
				showSnackbar(`Email del vendedor: ${sellerEmail}`, 8000);
			}
		} catch (err) {
			console.error('Error al abrir Google Chat con el vendedor:', err);
			showSnackbar('Error al buscar el email del vendedor');
		} finally {
			setOpeningGoogleChat(false);
		}
	};

	if (loading) {
		return (
			<Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
				<CircularProgress />
			</Container>
		);
	}

	if (error || !details) {
		return (
			<Container>
				<Alert severity="error" sx={{ mt: 3 }}>
					{error || 'No se pudieron cargar los detalles'}
				</Alert>
				<Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
					Volver
				</Button>
			</Container>
		);
	}

	return (
		<Container maxWidth="xl" sx={{ py: 3 }}>
			<Box sx={{ mb: 3 }}>
				<Button startIcon={<ArrowBackIcon />} onClick={handleBack} variant="outlined">
					Volver
				</Button>
			</Box>

			<TransactionHeader
				transaction={details.transaction.transactionData}
				saleOrder={details.saleOrder}
				flagShowCardInfo={details.transaction.flagShowCardInfo}
				finalizations={details.finalizationsData}
				authorizations={details.authorizations}
				onCopySuccess={handleCopySuccess}
			/>

			{showDLocalFinalizeBanner && details.transaction.transactionData.pendingAuthorization > 0 && (
				<Alert
					severity="success"
					sx={{ mb: 3 }}
					action={
						<Button color="inherit" size="small" variant="outlined" onClick={() => setShowFinalizeModal(true)}>
							Finalizar transacción
						</Button>
					}
				>
					<Typography variant="body2" fontWeight="bold" gutterBottom>
						Cobro DLocal registrado
					</Typography>
					<Typography variant="body2">
						El cobro al cliente se registró por DLocal. Finalizá la transacción para dar de baja el pendiente de
						autorización contra aerolínea.
					</Typography>
					{finalizeCopy && (
						<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
							{finalizeCopy.tooltip}
						</Typography>
					)}
				</Alert>
			)}

			{/* Botones de acción a nivel transacción */}
			<Box sx={{ display: 'flex', mb: 3, alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
					<Typography variant="subtitle2" color="text.secondary">
						Acciones:
					</Typography>
					{showDLocalCaptureButton && (
						<Tooltip title={dlocalFeeTooltip}>
							<Button
								size="small"
								variant="text"
								color="inherit"
								startIcon={<PaymentIcon sx={{ fontSize: 16 }} />}
								onClick={() => setShowDLocalDisclaimerModal(true)}
								sx={{ textTransform: 'none', fontSize: '0.8rem', py: 0.25, minWidth: 'auto' }}
							>
								Cobrar con DLocal
							</Button>
						</Tooltip>
					)}
					<Tooltip title="Copia un resumen con links y estado de autorización para pegar en Chat o mail">
						<Button
							size="small"
							variant="text"
							color="inherit"
							startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
							onClick={() => void handleCopyTransactionContext()}
							sx={{ textTransform: 'none', fontSize: '0.8rem', py: 0.25, minWidth: 'auto' }}
						>
							Copiar contexto
						</Button>
					</Tooltip>
					{showGoogleChatWithSellerButton && (
						<Tooltip title="Copia el email del vendedor y abre Google Chat para iniciar un mensaje directo">
							<span>
								<Button
									size="small"
									variant="text"
									color="inherit"
									startIcon={<ChatIcon sx={{ fontSize: 16 }} />}
									onClick={() => void handleOpenGoogleChatWithSeller()}
									disabled={openingGoogleChat}
									sx={{ textTransform: 'none', fontSize: '0.8rem', py: 0.25, minWidth: 'auto' }}
								>
									{openingGoogleChat ? 'Abriendo...' : 'Abrir Google Chat con vendedor'}
								</Button>
							</span>
						</Tooltip>
					)}
				</Box>

				<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
					<Tooltip title="Crear una nueva autorización contra un validating carrier">
						<Button
							ref={newAuthButtonRef}
							variant="outlined"
							color="primary"
							size="medium"
							startIcon={<AddIcon />}
							onClick={handleCreateAuthorization}
						>
							Nueva Autorización
						</Button>
					</Tooltip>

					<Tooltip
						title={
							finalizeCopy?.tooltip ??
							'Ajusta el monto de la transacción al total autorizado. El pendiente a autorizar quedará en 0.'
						}
					>
						<span>
							<Button
								variant="outlined"
								color="secondary"
								size="medium"
								startIcon={<CheckCircleIcon />}
								onClick={() => setShowFinalizeModal(true)}
								disabled={details.transaction.transactionData.pendingAuthorization === 0}
							>
								Finalizar Transacción
							</Button>
						</span>
					</Tooltip>
				</Box>
			</Box>

			<Box sx={{ mb: 2 }}>
				<Typography variant="h5" gutterBottom>
					Autorizaciones
				</Typography>
				<Typography variant="body2" color="text.secondary">
					Total: {details.authorizations.length} autorizaciones
				</Typography>
			</Box>

			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
				{details.authorizations.length === 0 ? (
					<Alert severity="info">No hay autorizaciones registradas aún</Alert>
				) : (
					details.authorizations.map((auth) => {
						const isHighlighted = resolvedHighlightAuthorizationId === auth.id;
						return (
							<Box key={auth.id} ref={isHighlighted ? highlightRef : null}>
								<AuthorizationCard
									authorization={auth}
									isHighlighted={isHighlighted}
									highlightEticket={isHighlighted ? highlightEticket : undefined}
									operators={operators}
									dealId={details.transaction.transactionData.crmTicket}
									saleOrderId={details.saleOrder?.id}
									onRefundNoteError={(message) => setSnackbar({ open: true, message })}
									onCopySuccess={handleCopySuccess}
									onAddETicket={handleAddETicket}
									transactionViewContext={transactionViewContext}
									onUnbindCross={handleUnbindCross}
									onRefund={handleRefund}
									onGenerateCommand={handleGenerateCommand}
								/>
							</Box>
						);
					})
				)}
			</Box>

			<TransactionFinalizationsDetail
				finalizations={details.finalizationsData}
				coinCode={details.transaction.transactionData.coinCode}
			/>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={snackbar.duration ?? 2000}
				onClose={handleCloseSnackbar}
				message={snackbar.message}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			/>

			<DLocalCaptureDisclaimerModal
				open={showDLocalDisclaimerModal}
				pendingAuthorization={details.transaction.transactionData.pendingAuthorization}
				coinCode={details.transaction.transactionData.coinCode}
				dealId={details.transaction.transactionData.crmTicket}
				saleOrderId={details.saleOrder?.id}
				onConfirm={handleDLocalDisclaimerConfirm}
				onClose={() => setShowDLocalDisclaimerModal(false)}
			/>

			<DLocalCaptureModal
				open={showDLocalCaptureModal}
				transactionId={transactionId}
				details={details}
				onClose={() => setShowDLocalCaptureModal(false)}
				onPaymentSuccess={handleDLocalPaymentSuccess}
				onCopied={handleCopySuccess}
			/>

			{/* Modal de crear autorización */}
			{details && (
				<CreateAuthorizationModal
					transactionId={transactionId}
					transactionContext={{
						transactionId,
						coinCode: details.transaction.transactionData.coinCode,
						pendingAuthorization: details.transaction.transactionData.pendingAuthorization,
						fee: details.transaction.transactionData.fee,
						cardStamp: details.transaction.transactionData.cardStamp,
						cardholder: details.transaction.transactionData.cardholder,
						document: details.transaction.transactionData.document,
						cardNumberFormatted: details.transaction.transactionData.cardNumberFormatted,
						cardExpiration: details.transaction.transactionData.cardExpiration,
						cardSecurityCodeFormatted: details.transaction.transactionData.cardSecurityCodeFormatted,
						flagShowCardInfo: details.transaction.flagShowCardInfo,
					}}
					existingAuthorizations={details.authorizations.map((auth) => ({
						createdAt: auth.createdAt,
						chargedAgainstOptionCode: auth.chargedAgainstOptionCode,
						chargedAgainstOptionName: auth.chargedAgainstOptionName,
					}))}
					open={showCreateAuthorizationModal}
					handleClose={() => setShowCreateAuthorizationModal(false)}
					reloadList={handleAuthorizationCreated}
				/>
			)}

			{/* Modal de confirmación para finalizar transacción */}
			<ConfirmationModal
				open={showFinalizeModal}
				title="Finalizar Transacción"
				message={
					finalizeCopy?.confirm ??
					'¿Estás seguro de finalizar esta transacción? El monto se ajustará al total autorizado y el pendiente a autorizar quedará en 0.'
				}
				handleAccept={handleFinalizeTransaction}
				handleClose={() => setShowFinalizeModal(false)}
				loading={processingFinalize}
			/>

			{transactionViewContext && (
				<UnbindCrossConfirmModal
					open={unbindTarget != null}
					cross={unbindTarget?.cross ?? null}
					authorizationId={unbindTarget?.authorizationId ?? 0}
					transactionContext={transactionViewContext}
					operators={operators}
					onClose={() => setUnbindTarget(null)}
					onSuccess={() => {
						setSnackbar({ open: true, message: 'Boleto desvinculado con éxito' });
						void loadData({ silent: true });
					}}
					onRequestRefund={(authId) => {
						setUnbindTarget(null);
						setSelectedAuthId(authId);
						setShowRefundModal(true);
					}}
					onRequestFinalize={() => {
						setUnbindTarget(null);
						setShowFinalizeModal(true);
					}}
				/>
			)}

			{/* Modales de acciones sobre autorizaciones */}
			{selectedAuthId && (
				<>
					<CreateETicketModal
						authorizationId={selectedAuthId}
						transactionId={transactionId}
						operators={operators}
						lockedOperatorCode={lockedCrossOperatorCode}
						pendingToCross={selectedAuthorizationCrossContext.pendingToCross}
						coinCode={selectedAuthorizationCrossContext.coinCode}
						authorizationCreatedAt={selectedAuthorizationForRefund?.createdAt ?? ''}
						chargedAgainstOptionCode={selectedAuthorizationForRefund?.chargedAgainstOptionCode}
						chargedAgainstOptionName={selectedAuthorizationForRefund?.chargedAgainstOptionName}
						open={showETicketModal}
						reloadList={() => handleModalClose('E-Ticket agregado con éxito')}
						handleClose={() => handleModalClose()}
					/>

					<CreateRefundAuthorizationModal
						transactionId={transactionId}
						authorizationId={selectedAuthId ?? undefined}
						coinCode={selectedAuthorizationForRefund?.coinCode}
						refundLimits={selectedRefundLimits}
						dealId={details.transaction.transactionData.crmTicket}
						saleOrderId={details.saleOrder?.id}
						onNavigationError={(message) => setSnackbar({ open: true, message })}
						open={showRefundModal}
						reloadList={() => handleModalClose('Devolución creada con éxito')}
						handleClose={() => handleModalClose()}
					/>

					<AuthorizationAmadeusFPCashCommandModal
						authorizationId={selectedAuthId}
						open={showCommandModal}
						handleClose={() => handleModalClose()}
					/>
				</>
			)}
		</Container>
	);
};
